import { PreviewCard as BasePreviewCard } from '@base-ui/react/preview-card';
import type { MouseEventHandler, ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';
import { popupPlacement, type PopupPlacement } from './popupPlacement';

type PreviewCardProps = {
  /** Hover/focus trigger. Usually a link chip or citation pill. */
  children: ReactNode;
  /** Preview shown in the card. Must also be available via the trigger destination. */
  content: ReactNode;
  /** Delay before opening, in ms. */
  delay?: number;
  /** Delay before closing, in ms. */
  closeDelay?: number;
  onOpen?: () => void;
  onOpenChange?: (open: boolean) => void;
  /** Fires as soon as the pointer enters the trigger (before `delay`). */
  onTriggerHover?: () => void;
  onTriggerClick?: MouseEventHandler<HTMLElement>;
  className?: string;
  triggerClassName?: string;
  placement?: PopupPlacement;
  offset?: [number, number];
};

const popupClassName = 'relative bg-white border-[2px] border-border-default rounded-lg shadow-md outline-none';

const arrowClassName = [
  'block relative z-10 w-4 h-2 overflow-clip',
  'data-[side=bottom]:-top-2 data-[side=top]:-bottom-2 data-[side=top]:rotate-180',
  'data-[side=left]:-right-3 data-[side=left]:rotate-90',
  'data-[side=right]:-left-3 data-[side=right]:-rotate-90',
  "before:content-[''] before:absolute before:bottom-0 before:left-1/2 before:box-border",
  'before:h-[11px] before:w-[11px] before:border-2 before:border-border-default before:bg-white',
  'before:-translate-x-1/2 before:translate-y-1/2 before:rotate-45'
].join(' ');

export default function PreviewCard({
  children,
  content,
  delay = 150,
  closeDelay = 150,
  onOpen,
  onOpenChange,
  onTriggerHover,
  onTriggerClick,
  className,
  triggerClassName = 'inline align-baseline',
  placement = 'bottom',
  offset = [0, 6]
}: PreviewCardProps) {
  const { side, align } = popupPlacement(placement);
  const [skidding, distance] = offset;

  return (
    <BasePreviewCard.Root
      onOpenChange={(next) => {
        onOpenChange?.(next);
        if (next) onOpen?.();
      }}
    >
      <BasePreviewCard.Trigger
        delay={delay}
        closeDelay={closeDelay}
        render={(props) => (
          <span
            {...props}
            className={twMerge(triggerClassName, props.className)}
            onPointerEnter={(event) => {
              (props.onPointerEnter as ((e: typeof event) => void) | undefined)?.(event);
              onTriggerHover?.();
            }}
            onFocus={(event) => {
              (props.onFocus as ((e: typeof event) => void) | undefined)?.(event);
              onTriggerHover?.();
            }}
            onClick={(event) => {
              (props.onClick as ((e: typeof event) => void) | undefined)?.(event);
              onTriggerClick?.(event);
            }}
          >
            {children}
          </span>
        )}
      />
      <BasePreviewCard.Portal>
        <BasePreviewCard.Positioner
          side={side}
          align={align}
          alignOffset={skidding}
          sideOffset={distance}
          collisionPadding={8}
          className="z-critical"
        >
          <BasePreviewCard.Popup className={twMerge(popupClassName, className)}>
            <BasePreviewCard.Arrow className={arrowClassName} />
            {content}
          </BasePreviewCard.Popup>
        </BasePreviewCard.Positioner>
      </BasePreviewCard.Portal>
    </BasePreviewCard.Root>
  );
}
